import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Zap, Info, Moon, Sun, X, RefreshCw, FileText, Scissors, ChevronLeft, ChevronRight } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import ToolsNav from "../components/ToolsNav";
import { RenderCMSContent } from "../components/CMSRenderer";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

// Set worker path
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function PdfSplit({ embedded = false }) {
  const [pdf, setPdf] = useState(null);
  const [pagePreviews, setPagePreviews] = useState([]);
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [splitMode, setSplitMode] = useState("selection"); // "selection", "range", "each"
  const [rangeStart, setRangeStart] = useState(1);
  const [rangeEnd, setRangeEnd] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPreviews, setIsLoadingPreviews] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pageContent, setPageContent] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/pdf-split`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
          document.title = data.seo?.meta_title || "Split PDF Files - KayTool";
        }
      } catch (error) {
        document.title = "Split PDF Files - KayTool";
      }
    };
    
    if (!embedded) {
      fetchPageContent();
    }
  }, [embedded]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );
    if (files.length > 0) {
      loadPdf(files[0]);
    } else {
      toast.error("Please drop a PDF file");
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      loadPdf(files[0]);
    }
    e.target.value = '';
  };

  const loadPdf = async (file) => {
    setIsLoadingPreviews(true);
    setProgress(0);
    
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const pageCount = pdfDoc.getPageCount();
      
      setPdf({
        name: file.name,
        file: file,
        arrayBuffer: arrayBuffer,
        pages: pageCount,
        size: file.size
      });
      
      setRangeEnd(pageCount);
      setSelectedPages(new Set());
      
      // Generate page previews using pdf.js
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdfJsDoc = await loadingTask.promise;
      
      const previews = [];
      for (let i = 1; i <= Math.min(pageCount, 50); i++) {
        setProgress(Math.round((i / Math.min(pageCount, 50)) * 100));
        
        const page = await pdfJsDoc.getPage(i);
        const scale = 0.3;
        const viewport = page.getViewport({ scale });
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        await page.render({ canvasContext: context, viewport }).promise;
        
        previews.push({
          pageNum: i,
          dataUrl: canvas.toDataURL('image/jpeg', 0.6),
          width: viewport.width,
          height: viewport.height
        });
      }
      
      setPagePreviews(previews);
      toast.success(`Loaded PDF with ${pageCount} pages`);
    } catch (error) {
      console.error("Error loading PDF:", error);
      toast.error("Failed to load PDF. Make sure it's a valid PDF file.");
    }
    
    setIsLoadingPreviews(false);
    setProgress(0);
  };

  const togglePage = (pageNum) => {
    setSelectedPages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pageNum)) {
        newSet.delete(pageNum);
      } else {
        newSet.add(pageNum);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    if (pdf) {
      const allPages = new Set(Array.from({ length: pdf.pages }, (_, i) => i + 1));
      setSelectedPages(allPages);
    }
  };

  const selectNone = () => {
    setSelectedPages(new Set());
  };

  const selectRange = () => {
    const start = Math.max(1, Math.min(rangeStart, pdf?.pages || 1));
    const end = Math.max(start, Math.min(rangeEnd, pdf?.pages || 1));
    const rangePages = new Set(Array.from({ length: end - start + 1 }, (_, i) => start + i));
    setSelectedPages(rangePages);
  };

  const splitPdf = async () => {
    if (!pdf) return;

    setIsProcessing(true);
    setProgress(0);

    try {
      const pdfDoc = await PDFDocument.load(pdf.arrayBuffer);
      
      if (splitMode === "each") {
        // Split into individual pages
        const zip = new JSZip();
        
        for (let i = 0; i < pdf.pages; i++) {
          setProgress(Math.round(((i + 1) / pdf.pages) * 90));
          
          const newPdf = await PDFDocument.create();
          const [page] = await newPdf.copyPages(pdfDoc, [i]);
          newPdf.addPage(page);
          
          const pdfBytes = await newPdf.save();
          const baseName = pdf.name.replace('.pdf', '');
          zip.file(`${baseName}_page_${i + 1}.pdf`, pdfBytes);
        }
        
        setProgress(95);
        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, `${pdf.name.replace('.pdf', '')}_split_pages.zip`);
        toast.success(`Split into ${pdf.pages} individual PDFs!`);
        
      } else if (splitMode === "selection" && selectedPages.size > 0) {
        // Extract selected pages
        const newPdf = await PDFDocument.create();
        const sortedPages = Array.from(selectedPages).sort((a, b) => a - b);
        
        for (let i = 0; i < sortedPages.length; i++) {
          setProgress(Math.round(((i + 1) / sortedPages.length) * 90));
          const pageIndex = sortedPages[i] - 1;
          const [page] = await newPdf.copyPages(pdfDoc, [pageIndex]);
          newPdf.addPage(page);
        }
        
        setProgress(95);
        const pdfBytes = await newPdf.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        saveAs(blob, `${pdf.name.replace('.pdf', '')}_pages_${sortedPages.join('-')}.pdf`);
        toast.success(`Extracted ${selectedPages.size} pages!`);
        
      } else if (splitMode === "range") {
        // Extract page range
        const start = Math.max(1, Math.min(rangeStart, pdf.pages));
        const end = Math.max(start, Math.min(rangeEnd, pdf.pages));
        
        const newPdf = await PDFDocument.create();
        
        for (let i = start - 1; i < end; i++) {
          setProgress(Math.round(((i - start + 2) / (end - start + 1)) * 90));
          const [page] = await newPdf.copyPages(pdfDoc, [i]);
          newPdf.addPage(page);
        }
        
        setProgress(95);
        const pdfBytes = await newPdf.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        saveAs(blob, `${pdf.name.replace('.pdf', '')}_pages_${start}-${end}.pdf`);
        toast.success(`Extracted pages ${start}-${end}!`);
      } else {
        toast.error("Please select pages to extract");
      }
      
      setProgress(100);
    } catch (error) {
      console.error("Split error:", error);
      toast.error("Failed to split PDF");
    }

    setIsProcessing(false);
    setProgress(0);
  };

  const clearPdf = () => {
    setPdf(null);
    setPagePreviews([]);
    setSelectedPages(new Set());
  };

  if (embedded) {
    return (
      <div className="p-4">
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            isDragging ? 'border-orange-500 bg-orange-50' : 'border-slate-300 hover:border-orange-400'
          }`}
        >
          <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
          <p className="text-lg font-medium">Drop a PDF file here or click to upload</p>
          <p className="text-sm text-slate-500 mt-2">Split or extract pages from PDF</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      <Toaster position="top-center" theme={darkMode ? "dark" : "light"} />
      
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                KayTool
              </h1>
            </a>
            <div className="flex items-center gap-3">
              <ToolsNav />
              <Badge variant="secondary" className="hidden md:flex items-center gap-1 px-3 py-1 dark:bg-slate-700">
                <Info className="w-3 h-3" />
                100% Local Processing
              </Badge>
              <Button variant="outline" size="icon" onClick={() => setDarkMode(prev => !prev)} className="rounded-full">
                {darkMode ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Ad Placement - Above the Fold (Below Header) */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <Badge className="mb-4 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
            PDF Split
          </Badge>
          <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Split PDF Files
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Extract specific pages or split PDF into individual files. Select pages visually or by range.
          </p>
        </div>

        {!pdf ? (
          /* Upload Area */
          <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6">
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                  isDragging 
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' 
                    : 'border-slate-300 dark:border-slate-600 hover:border-orange-400 dark:hover:border-orange-500'
                }`}
              >
                <Scissors className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                <p className="text-xl font-medium text-slate-700 dark:text-slate-300">
                  Drop a PDF file here or click to upload
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                  Extract pages or split into multiple PDFs
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
            </CardContent>
          </Card>
        ) : (
          <>
            {/* PDF Info & Controls */}
            <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <FileText className="w-10 h-10 text-red-500" />
                    <div>
                      <p className="font-medium dark:text-white">{pdf.name}</p>
                      <p className="text-sm text-slate-500">{pdf.pages} pages • {formatFileSize(pdf.size)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={clearPdf} className="gap-2">
                      <X className="w-4 h-4" /> Change PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Split Mode Selection */}
            <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="splitMode"
                      checked={splitMode === "selection"}
                      onChange={() => setSplitMode("selection")}
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm font-medium dark:text-white">Select Pages</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="splitMode"
                      checked={splitMode === "range"}
                      onChange={() => setSplitMode("range")}
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm font-medium dark:text-white">Page Range</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="splitMode"
                      checked={splitMode === "each"}
                      onChange={() => setSplitMode("each")}
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm font-medium dark:text-white">Split All (One PDF per Page)</span>
                  </label>
                </div>

                {splitMode === "range" && (
                  <div className="flex items-center gap-4 mt-4">
                    <div className="flex items-center gap-2">
                      <Label>From:</Label>
                      <Input
                        type="number"
                        min={1}
                        max={pdf.pages}
                        value={rangeStart}
                        onChange={(e) => setRangeStart(parseInt(e.target.value) || 1)}
                        className="w-20"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label>To:</Label>
                      <Input
                        type="number"
                        min={1}
                        max={pdf.pages}
                        value={rangeEnd}
                        onChange={(e) => setRangeEnd(parseInt(e.target.value) || 1)}
                        className="w-20"
                      />
                    </div>
                  </div>
                )}

                {splitMode === "selection" && (
                  <div className="flex items-center gap-2 mt-4">
                    <Button variant="outline" size="sm" onClick={selectAll}>Select All</Button>
                    <Button variant="outline" size="sm" onClick={selectNone}>Select None</Button>
                    <span className="text-sm text-slate-500 ml-2">
                      {selectedPages.size} of {pdf.pages} pages selected
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Loading Previews Progress */}
            {isLoadingPreviews && (
              <Card className="mb-6 dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Loading page previews...</span>
                    <span className="text-sm text-slate-500">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </CardContent>
              </Card>
            )}

            {/* Page Previews */}
            {splitMode === "selection" && pagePreviews.length > 0 && (
              <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                    {pagePreviews.map((preview) => (
                      <div
                        key={preview.pageNum}
                        onClick={() => togglePage(preview.pageNum)}
                        className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                          selectedPages.has(preview.pageNum)
                            ? 'border-orange-500 ring-2 ring-orange-200'
                            : 'border-slate-200 dark:border-slate-600 hover:border-orange-300'
                        }`}
                      >
                        <img
                          src={preview.dataUrl}
                          alt={`Page ${preview.pageNum}`}
                          className="w-full h-auto"
                        />
                        <div className={`absolute inset-0 flex items-center justify-center transition-all ${
                          selectedPages.has(preview.pageNum) ? 'bg-orange-500/20' : ''
                        }`}>
                          {selectedPages.has(preview.pageNum) && (
                            <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold">
                              ✓
                            </div>
                          )}
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs text-center py-1">
                          {preview.pageNum}
                        </div>
                      </div>
                    ))}
                  </div>
                  {pdf.pages > 50 && (
                    <p className="text-xs text-slate-500 mt-3 text-center">
                      Showing first 50 pages. All {pdf.pages} pages will be processed.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Processing Progress */}
            {isProcessing && (
              <Card className="mb-6 dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Splitting PDF...</span>
                    <span className="text-sm text-slate-500">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </CardContent>
              </Card>
            )}

            {/* Split Button */}
            <div className="flex justify-center">
              <Button 
                onClick={splitPdf} 
                disabled={isProcessing || (splitMode === "selection" && selectedPages.size === 0)}
                className="gap-2 bg-orange-600 hover:bg-orange-700 px-8 py-6 text-lg"
              >
                <Scissors className="w-5 h-5" /> 
                {splitMode === "each" 
                  ? `Split into ${pdf.pages} PDFs` 
                  : splitMode === "range"
                    ? `Extract Pages ${rangeStart}-${rangeEnd}`
                    : `Extract ${selectedPages.size} Page${selectedPages.size !== 1 ? 's' : ''}`
                }
              </Button>
            </div>
          </>
        )}

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {[
            { title: "Visual Page Selection", desc: "See page thumbnails and click to select exactly which pages you need" },
            { title: "Multiple Split Modes", desc: "Extract by selection, page range, or split every page into separate PDFs" },
            { title: "100% Private", desc: "All processing happens in your browser - files never leave your device" }
          ].map((feature, i) => (
            <Card key={i} className="dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <h4 className="font-semibold mb-2 dark:text-white">{feature.title}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Ad Placement - After Features */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      {/* CMS Content */}
      {pageContent?.content && (
        <div className="bg-white dark:bg-slate-800 py-12">
          <div className="container mx-auto px-4">
            <RenderCMSContent content={pageContent.content} />
          </div>
        </div>
      )}

      {/* Ad Placement - Before Footer */}
      <AdContainer className="my-6">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      {/* Sticky Mobile Ad */}
      <StickyMobileAd slot="sticky-footer" />

    </div>
  );
}

export default PdfSplit;
